import dayjs from "dayjs";
import {
  ALLOWED_MESSAGE_FILE_TYPES,
  IMessageAttachmentRest,
  IMessageMetadata,
  IMessageRest,
  MAX_FILE_SIZE,
  MessageAttachment,
  MessageRecords,
  MessageTypeEnum,
} from "./types";
import { Service } from "../types";

export const DATE_TIME_FORMAT = "hh:mma DD/MM/YY";

export const getNameAbbreviation = (fullName: string): string => {
  if (!fullName) return "";
  return fullName
    .split(" ")
    .filter(Boolean) // Remove empty strings caused by consecutive spaces
    .map((word) => word[0].toUpperCase())
    .join("");
};

export const formatMessageDateTime = (date: Date) => {
  return dayjs(date).format(DATE_TIME_FORMAT);
};

export const fromMessageRestsToMessageRecords = (
  messageRests: IMessageRest[]
): MessageRecords => {
  return messageRests.map((messageRecordRest) => {
    return {
      id: messageRecordRest.id,
      journeyId: messageRecordRest.journey_id,
      text: messageRecordRest.text,
      authorEmail: messageRecordRest.author_email,
      authorName: messageRecordRest.author_name,
      direction: messageRecordRest.direction,
      created: new Date(messageRecordRest.created),
      updated: messageRecordRest.updated
        ? new Date(messageRecordRest.updated)
        : null,
      updater: messageRecordRest.updater,
      processed: messageRecordRest.processed,
      attachmentsMeta: messageRecordRest.attachments_meta
        ? fromMessageAttachmentRestsToMessageAttachments(
            messageRecordRest.attachments_meta
          )
        : null,
      type: messageRecordRest.type,
    };
  });
};

export const fromMessageAttachmentRestsToMessageAttachments = (
  messageAttachmentRests: IMessageAttachmentRest[]
): MessageAttachment[] => {
  return messageAttachmentRests.map((messageAttachmentRest) => {
    return {
      id: messageAttachmentRest.id,
      fileUploadType: messageAttachmentRest.file_upload_type,
      fileUploadTypeAffix: messageAttachmentRest.file_upload_type_affix,
      fileName: messageAttachmentRest.file_name,
      link: messageAttachmentRest.link,
    };
  });
};

export const isMessageAttachmentValid = ({
  file,
  handleError,
}: {
  file?: File;
  handleError: (message: string) => void;
}): boolean => {
  if (!file) return false;
  if (file.size > MAX_FILE_SIZE) {
    handleError("File too large; maximum 5MB");
    return false;
  }
  if (!ALLOWED_MESSAGE_FILE_TYPES.includes(file.type)) {
    handleError(
      "Accepted types: pdf, doc/docx, jpg/jpeg, png, heif, webp, xls/xlsm/xlsx."
    );
    return false;
  }
  return true;
};

export const fromMessageAttachmentToMessageAttachmentRest = (
  attachment: MessageAttachment
): IMessageAttachmentRest => ({
  file_upload_type: attachment.fileUploadType,
  file_name: attachment.fileName,
});

export const toMessageRequestFormData = (
  journey_id: string,
  text: string,
  type: MessageTypeEnum,
  attachments?: MessageAttachment[]
): FormData => {
  const formData = new FormData();
  const attachments_meta = attachments?.map((attachment) =>
    fromMessageAttachmentToMessageAttachmentRest(attachment)
  );
  const messageRest = {
    ...(type === MessageTypeEnum.INTERNET
      ? { request_id: journey_id }
      : { journey_id }),
    text,
    attachments_meta,
    type,
  } as IMessageRest;
  formData.append(
    "message",
    new Blob([JSON.stringify(messageRest)], { type: "application/json" })
  );
  attachments?.forEach((attachment) =>
    formData.append("files", attachment.file!)
  );
  return formData;
};

export const fromServiceToMessageMetadata = (
  service: Service
): IMessageMetadata => {
  return {
    requestId: service.request_id,
    journeyId: service.journey_id,
    rawRequestStatus: service.raw_request_status,
    latestMessageDirection: service.latest_message_direction,
    latestMessageProcessed: service.latest_message_processed,
    latestMessageType: service.latest_message_type,
    hasUnreadMessageForUser: service.has_unread_message_for_user,
    messagePause: service.message_pause,
    messageEnabled: service.message_enabled,
    serviceType: service.service_type,
    connectionType: service.connection_type,
    type:
      service.service_type === "INTERNET"
        ? MessageTypeEnum.INTERNET
        : MessageTypeEnum.STANDARD,
  };
};
